import { AssetSortSchema, CERTIFIED_STATUS, SECTION_KEYS, type ContextRunStatus, type PipelineSummary, AssetStatusFilterSchema, type AssetSearch, type AssetSort, type AssetStatusFilter, type SectionKey, type SubjectDraft, type VersionDetail } from '@lastrouter/contracts';

/** Parte do contexto preenchida: seção com texto, ou camada semântica com métrica. */
export function partFilled(subject: SubjectDraft | null, part: SectionKey | 'semantica'): boolean {
  if (!subject) return false;
  if (part === 'semantica') return subject.metricas.length > 0;
  return Boolean(subject.secoes[part]?.trim());
}

export type PublishState = 'sem_rascunho' | 'pendencias' | 'nao_salvo' | 'aguardando' | 'pronto';

/** Publicar só com rascunho salvo, sem pendências e com as aprovações dadas. */
export function publishState(draft: VersionDetail | null, dirty: boolean): PublishState {
  if (!draft) return 'sem_rascunho';
  if (draft.issues.length) return 'pendencias';
  if (dirty) return 'nao_salvo';
  if (!draft.status.ready) return 'aguardando';
  return 'pronto';
}

// ---------- Lista de apps: estado na URL ----------

export const APP_STATUSES = AssetStatusFilterSchema.options;
export const APP_PAGE_SIZES = [25, 50, 100] as const;

/** Estado da lista de apps na URL: o link e o voltar do navegador abrem a mesma lista. */
export interface AppsSearch {
  q?: string;
  status?: AssetStatusFilter;
  /** Uuid do cliente ou 'none' (sem cliente). */
  client?: string;
  source?: string;
  sort?: AssetSort;
  page?: number;
  size?: number;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Só os valores válidos entram; o resto some da URL e a lista abre no padrão. */
export function parseAppsSearch(s: Record<string, unknown>): AppsSearch {
  const out: AppsSearch = {};
  const q = typeof s.q === 'string' ? s.q.trim().slice(0, 200) : '';
  if (q) out.q = q;
  if (APP_STATUSES.includes(s.status as AssetStatusFilter)) out.status = s.status as AssetStatusFilter;
  if (typeof s.client === 'string' && (s.client === 'none' || UUID_RE.test(s.client))) out.client = s.client;
  if (typeof s.source === 'string' && /^[a-z0-9][a-z0-9_-]{0,63}$/i.test(s.source)) out.source = s.source;
  if (AssetSortSchema.options.includes(s.sort as AssetSort) && s.sort !== 'name') out.sort = s.sort as AssetSort;
  const page = Number(s.page);
  if (Number.isInteger(page) && page > 1) out.page = page;
  const size = Number(s.size);
  if ((APP_PAGE_SIZES as readonly number[]).includes(size) && size !== APP_PAGE_SIZES[0]) out.size = size;
  return out;
}

/** Consulta da API para o estado da URL. Dentro da tela de um cliente, o cliente é fixo. */
export function appsQuery(s: AppsSearch, fixedClient?: string): AssetSearch {
  const client = fixedClient ?? s.client;
  return {
    ...(s.q ? { q: s.q } : {}),
    ...(s.status ? { status: s.status } : {}),
    ...(client ? { client_id: client } : {}),
    ...(s.source ? { source_id: s.source } : {}),
    sort: s.sort ?? 'name',
    page: s.page ?? 1,
    page_size: s.size ?? APP_PAGE_SIZES[0],
  };
}

/** Clique no cabeçalho: a mesma coluna inverte; outra começa no sentido natural (recarga: mais nova primeiro). */
export function nextSort(current: AssetSort | undefined, field: 'name' | 'status' | 'client' | 'last_reload'): AssetSort {
  const cur = current ?? 'name';
  if (cur === field) return `-${field}` as AssetSort;
  if (cur === `-${field}`) return field;
  return field === 'last_reload' ? '-last_reload' : field;
}

/** Algum filtro além da ordem e da página: o vazio vira "nenhum resultado" com botão de limpar. */
export const hasFilters = (s: AppsSearch, fixedClient?: string): boolean => Boolean(s.q || s.status || s.source || (!fixedClient && s.client));

// ---------- Abas da página do app ----------

/** Rótulo do selo quando a certificação libera o contexto (texto que o pipeline grava). */
export const CERTIFIED = CERTIFIED_STATUS;

/** Abas da página do app, na ordem do pipeline. Valores da URL (`?tab=`), em inglês. */
// A aba Validação do cliente saiu em 29/10/2026: o gabarito, na aba Bateria, diz o que é a resposta certa.
export const ASSET_TABS = ['summary', 'risks', 'context', 'battery', 'publish', 'quality', 'versions'] as const;
export type AssetTab = (typeof ASSET_TABS)[number];
export type TabState = 'done' | 'pending' | 'blocked';

export interface AssetTabsInput {
  /** Situação da geração que as abas mostram; null quando o app nunca foi gerado. */
  runStatus: ContextRunStatus | null;
  /** Há uma geração rodando agora (pode não ser a que as abas mostram). */
  running: boolean;
  pipeline: PipelineSummary | null;
  /** Achados de gravidade crítica na geração antiga (o pipeline traz a contagem no resumo). */
  criticalFindings: number;
  /** O contexto do app (rascunho ou publicado) já tem a camada semântica. */
  contextFilled: boolean;
  draft: VersionDetail | null;
  /** Publicado e sem rascunho aberto. */
  published: boolean;
}

/**
 * Selo de cada aba (feito, pendente ou bloqueado; Versões não tem) e a aba que abre quando a URL não diz:
 * o próximo passo. Sem geração terminada, o próximo passo é gerar, no Resumo.
 */
export function assetTabs(g: AssetTabsInput): { tabs: Record<AssetTab, TabState | null>; initial: AssetTab } {
  const ok = g.runStatus === 'ok';
  const p = ok ? g.pipeline : null;
  const approved = p?.battery?.by_status.aprovado ?? 0;
  const refused = g.draft?.version.approvals.some((a) => a.decision === 'recusado' && !a.no_lugar_de) ?? false;
  const tabs: Record<AssetTab, TabState | null> = {
    summary: g.running || g.runStatus === null ? 'pending' : ok ? 'done' : 'blocked',
    risks: !ok ? 'blocked' : (p ? p.risks.critical : g.criticalFindings) > 0 ? 'pending' : 'done',
    context: g.contextFilled ? 'done' : 'pending',
    // Sem caso aprovado a aba fica pendente, não bloqueada: o gabarito é preenchido nela.
    battery: !p ? 'blocked' : p.certification?.status === CERTIFIED_STATUS ? 'done' : 'pending',
    publish: g.published ? 'done' : !g.draft || g.draft.issues.length > 0 || refused ? 'blocked' : 'pending',
    quality: null,
    versions: null,
  };
  if (!ok) return { tabs, initial: 'summary' };
  const order: AssetTab[] = ['context', 'battery', 'publish'];
  return { tabs, initial: order.find((t) => tabs[t] !== 'done') ?? 'summary' };
}

/*
 * Endereços de antes das abas (até 28/10/2026): cada seção tinha a sua parte (`?tab=rules`), o Gerar contexto
 * tinha as abas dele (`?tab=generate&step=...`) e Versões e Histórico eram partes separadas. `parte` é o parâmetro
 * ainda mais antigo, em português. undefined: a página abre no próximo passo.
 */
const OLD_TAB: Record<string, AssetTab | undefined> = {
  rules: 'context',
  assumptions: 'context',
  restrictions: 'context',
  vocabulary: 'context',
  synonyms: 'context',
  examples: 'context',
  corrections: 'context',
  semantic: 'context',
  history: 'versions',
  validation: 'battery',
  generate: undefined,
};
const OLD_STEP: Record<string, AssetTab> = { summary: 'summary', risks: 'risks', proposal: 'context', validation: 'battery', battery: 'battery', files: 'summary', publish: 'publish' };
const OLD_PARTE: Record<string, AssetTab> = {
  ...Object.fromEntries(SECTION_KEYS.map((k) => [k, 'context' as const])),
  semantica: 'context',
  versoes: 'versions',
  historico: 'versions',
};

export const isOldAssetTab = (tab: string): boolean => tab in OLD_TAB;

/** Aba nova para um endereço antigo do app. */
export function legacyAssetTab(s: { tab?: string | undefined; step?: string | undefined; parte?: string | undefined }): AssetTab | undefined {
  if (s.parte !== undefined) return OLD_PARTE[s.parte];
  if (s.tab === 'generate') return s.step ? OLD_STEP[s.step] : undefined;
  if (ASSET_TABS.includes(s.tab as AssetTab)) return s.tab as AssetTab;
  return s.tab ? OLD_TAB[s.tab] : undefined;
}

// ---------- Hoje × proposta ----------

export interface DiffLine {
  kind: 'same' | 'add' | 'del';
  text: string;
}

/** Diferença linha a linha do texto de hoje para o proposto (maior subsequência comum). */
export function lineDiff(before: string, after: string): DiffLine[] {
  const a = before ? before.split('\n') : [];
  const b = after ? after.split('\n') : [];
  const del = (text: string): DiffLine => ({ kind: 'del', text });
  const add = (text: string): DiffLine => ({ kind: 'add', text });
  // ponytail: tabela O(n·m); acima de um milhão de células, mostra tudo saindo e tudo entrando.
  if (a.length * b.length > 1_000_000) return [...a.map(del), ...b.map(add)];
  const lcs = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i--)
    for (let j = b.length - 1; j >= 0; j--) lcs[i]![j] = a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ kind: 'same', text: a[i]! });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) out.push(del(a[i++]!));
    else out.push(add(b[j++]!));
  }
  return [...out, ...a.slice(i).map(del), ...b.slice(j).map(add)];
}
